import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { useTheme } from '../contexts/ThemeContext';
import {
  LayoutDashboard, Megaphone, FolderOpen, Bot, Users,
  BarChart3, FileText, Mail, Palette, Settings, Plus,
  Shield, Sun, Moon, Zap, Rocket, AlertTriangle,
  Briefcase, Newspaper, ShieldAlert, HardDrive, Workflow
} from 'lucide-react';

const NAV_SECTIONS = [
  {
    label: 'Main',
    items: [
      { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
      { path: '/campaign-builder', icon: Megaphone, label: 'Campaign Builder' },
      { path: '/campaigns', icon: FolderOpen, label: 'Campaigns' },
    ]
  },
  {
    label: 'Intelligence',
    items: [
      { path: '/agents', icon: Bot, label: 'Agent Studio' },
      { path: '/workflow-builder', icon: Workflow, label: 'Workflow Builder' },
      { path: '/analytics', icon: BarChart3, label: 'Analytics' },
    ]
  },
  {
    label: 'Operations',
    items: [
      { path: '/contacts', icon: Users, label: 'Contacts' },
      { path: '/forms', icon: FileText, label: 'Forms' },
      { path: '/leads', icon: Mail, label: 'Leads' },
      { path: '/content-library', icon: HardDrive, label: 'Content Library' },
      { path: '/deliverability', icon: Shield, label: 'Deliverability' },
    ]
  },
  {
    label: 'System',
    items: [
      { path: '/branding', icon: Palette, label: 'Branding' },
      { path: '/settings', icon: Settings, label: 'Settings' },
    ]
  }
];

const MATERIAL_OPTIONS = [
  { value: 'urgent_alert', label: 'Urgent Security Alert' },
  { value: 'executive_risk_brief', label: 'Executive Risk Brief' },
  { value: 'security_newsletter', label: 'Security Newsletter' },
  { value: 'threat_intelligence', label: 'Threat Intelligence Report' },
  { value: 'product_launch', label: 'Product Launch' },
  { value: 'webinar_invitation', label: 'Webinar Invitation' },
];

export function GlobalSidebar({ onNewCampaign, onCreateCampaign }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newTopic, setNewTopic] = useState('');
  const [newMaterial, setNewMaterial] = useState('urgent_alert');
  const [creating, setCreating] = useState(false);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleCreateSubmit = async () => {
    if (!newTopic.trim()) return;
    setCreating(true);
    try {
      if (onCreateCampaign) {
        await onCreateCampaign(newTopic.trim(), newMaterial);
      }
      setDialogOpen(false);
      setNewTopic('');
      setNewMaterial('urgent_alert');
    } catch (err) {
      // Error is handled by the parent callback
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <aside
        data-testid="global-sidebar"
        className="fixed left-0 top-0 h-screen w-[220px] bg-[hsl(var(--sidebar))] text-[hsl(var(--sidebar-foreground))] border-r border-white/[0.06] flex flex-col z-50"
      >
        {/* Brand block */}
        <div className="flex items-center gap-2.5 h-14 px-4 border-b border-white/[0.06] flex-shrink-0" data-testid="global-sidebar-brand">
          <div className="w-8 h-8 rounded-lg bg-[hsl(var(--brand))] flex items-center justify-center flex-shrink-0">
            <Shield className="w-4 h-4 text-[hsl(var(--brand-foreground))]" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground leading-tight truncate">CipherCampaign</p>
            <p className="text-[10px] text-muted-foreground/60 leading-tight">Command Center</p>
          </div>
        </div>

        {/* New Campaign CTA — opens dialog */}
        <div className="px-3 pt-3 pb-1 flex-shrink-0">
          <Button
            data-testid="global-sidebar-new-campaign-button"
            className="w-full h-9 gap-2 text-sm font-medium rounded-xl bg-[hsl(var(--accent-warm))] text-[hsl(var(--accent-warm-foreground))] hover:bg-[hsl(var(--accent-warm)/0.88)] active:scale-[0.98] transition-transform duration-150"
            onClick={() => setDialogOpen(true)}
          >
            <Plus className="w-4 h-4" />
            New Campaign
          </Button>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 px-2 py-2">
          <nav data-testid="global-sidebar-nav">
            {NAV_SECTIONS.map((section, si) => (
              <div key={section.label} className={si > 0 ? 'mt-5' : ''}>
                <p className="px-3 mb-1.5 text-[10px] uppercase tracking-[0.16em] font-semibold text-muted-foreground/40">
                  {section.label}
                </p>
                <div className="space-y-0.5">
                  {section.items.map(item => {
                    const Icon = item.icon;
                    const active = isActive(item.path);
                    return (
                      <button
                        key={item.path}
                        data-testid={`global-sidebar-nav-item-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        onClick={() => navigate(item.path)}
                        className={`relative w-full flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium transition-colors duration-150 ${
                          active
                            ? 'bg-white/[0.08] text-foreground'
                            : 'text-muted-foreground hover:text-foreground hover:bg-white/[0.04]'
                        }`}
                      >
                        {active && (
                          <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r bg-[hsl(var(--brand))] animate-scale-in" />
                        )}
                        <Icon className={`w-4 h-4 flex-shrink-0 ${active ? 'text-[hsl(var(--brand))]' : ''}`} />
                        <span className="truncate">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>
        </ScrollArea>

        {/* Footer: Securado Copyright Badge + Theme toggle */}
        <div className="border-t border-white/[0.06] px-3 py-2.5 flex-shrink-0 space-y-1.5" data-testid="global-sidebar-footer">
          <button
            data-testid="global-sidebar-theme-toggle"
            onClick={toggleTheme}
            className="w-full flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] text-muted-foreground hover:text-foreground hover:bg-white/[0.04] transition-colors duration-150"
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
          </button>
          {/* Securado Copyright Badge */}
          <div className="flex items-center gap-2 px-3 py-1" data-testid="securado-copyright-badge">
            <div className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0">
              <svg className="w-3.5 h-3.5 text-muted-foreground/40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>
            <span className="text-[10px] text-muted-foreground/40 leading-tight">
              Powered by Securado
            </span>
          </div>
        </div>
      </aside>

      {/* ─── New Campaign Dialog ─── */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[480px] bg-[hsl(var(--card))] border-white/10" data-testid="new-campaign-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Launch New Campaign</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Describe the campaign topic and choose a material type. Our AI pipeline will handle the rest.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Campaign Topic</label>
              <Input
                data-testid="new-campaign-topic-input"
                placeholder="e.g. Urgent threat alert about a new ransomware variant..."
                value={newTopic}
                onChange={(e) => setNewTopic(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleCreateSubmit();
                  }
                }}
                className="h-11 bg-[hsl(var(--background))] border-white/10"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Material Type</label>
              <Select value={newMaterial} onValueChange={setNewMaterial}>
                <SelectTrigger className="h-11 bg-[hsl(var(--background))] border-white/10" data-testid="new-campaign-material-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {MATERIAL_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="border-white/10"
              data-testid="new-campaign-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateSubmit}
              disabled={!newTopic.trim() || creating}
              className="bg-[hsl(var(--accent-warm))] text-[hsl(var(--accent-warm-foreground))] hover:bg-[hsl(var(--accent-warm)/0.88)] gap-2"
              data-testid="new-campaign-launch-btn"
            >
              {creating ? (
                <><Zap className="w-4 h-4 animate-spin" /> Launching...</>
              ) : (
                <><Rocket className="w-4 h-4" /> Launch Campaign</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
